/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useCallback, useRef, useEffect, useState, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { NOOP } from '../../../utils/const';
import { getElementSize } from '../../Utils/documentUtils';

function getBreadcrumbObject(breadcrumb) {
  if (typeof breadcrumb === 'string') {
    return { id: breadcrumb, label: breadcrumb, selectable: true };
  }
  return breadcrumb;
}

const ListItem = ({
  blockClassName, last, breadcrumb, onSelect, setWidth,
}) => {
  const ref = useRef();

  const handleSelect = useCallback((evt) => {
    onSelect(breadcrumb, evt);
  }, [breadcrumb, onSelect]);

  useEffect(() => {
    if (ref.current) {
      setWidth(getElementSize(ref.current).width);
    }
  }, [setWidth]);

  const breadcrumbObj = getBreadcrumbObject(breadcrumb);
  const selectable = breadcrumbObj.selectable === false ? undefined : handleSelect;

  const labelClasses = cns(`${blockClassName}__label`, {
    [`${blockClassName}__label--last`]: last,
    [`${blockClassName}__label--selectable`]: selectable,
  });
  return (
    <li ref={ref} className={`${blockClassName}__list-item`}>
      <span role="menuitem" tabIndex={0} className={labelClasses} onClick={selectable ? handleSelect : undefined}>
        {breadcrumbObj.label}
      </span>
      {!last && <span>&nbsp;&#x203A;&nbsp;</span>}
    </li>
  );
};

ListItem.propTypes = {
  blockClassName: PropTypes.string.isRequired,
  last: PropTypes.bool.isRequired,
  breadcrumb: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ]).isRequired,
  onSelect: PropTypes.func.isRequired,
  setWidth: PropTypes.func,
};

ListItem.defaultProps = {
  setWidth: NOOP,
};
const CollapsedListItem = ({ blockClassName }) => (
  <li className={`${blockClassName}__list-item ${blockClassName}__list-item--collapsed`} key="collapsed">
    <span role="menuitem" className={`${blockClassName}__label`}>
      ...
    </span>
    <span>&nbsp;&#x203A;&nbsp;</span>
  </li>
);

CollapsedListItem.propTypes = {
  blockClassName: PropTypes.string.isRequired,
};

function useCollapsing(maxWidth, items) {
  const [collapsed, setCollapsed] = useState(false);

  const data = useMemo(() => {
    setCollapsed(false);
    const result = { width: 0, items: 0 };
    result.itemWidthCallback = (width) => {
      result.width += width;
      result.items += 1;
      setCollapsed(items && items.length > 2 && maxWidth < result.width);
    };
    return result;
  }, [maxWidth, items]);

  if (!maxWidth) return { collapsed, itemWidthCallback: NOOP };
  return { collapsed, itemWidthCallback: data.itemWidthCallback };
}

/**
 * Le breadcrumb tengono traccia del flusso di navigazione dell'utente a partire dalla prima schermata aperta.
 *
 * Le voci della breadcrumb sono cliccabili.
 *
 * Ogni step delle breadcrumbs deve contenere una label molto predittiva.
 *
 * Quando le breadcrumbs superano lo spazio previsto nella toolbar tutte le fasi intermedie
 * tra il punto di partenza e quello di arrivo vengono sostituite con il simbolo dei tre puntini.
 */
const Breadcrumbs = ({
  blockClassName, className, breadcrumbs, onSelect, maxWidth,
}) => {
  const classes = cns(blockClassName, className);
  const { collapsed, itemWidthCallback } = useCollapsing(maxWidth, breadcrumbs);

  if (collapsed && breadcrumbs.length >= 2) {
    return (
      <ol className={classes}>
        <ListItem
          breadcrumb={breadcrumbs[0]}
          onSelect={onSelect}
          last={false}
          blockClassName={blockClassName}
        />
        <CollapsedListItem blockClassName={blockClassName} />
        <ListItem
          breadcrumb={breadcrumbs[breadcrumbs.length - 1]}
          last
          onSelect={onSelect}
          blockClassName={blockClassName}
        />
      </ol>
    );
  }
  return (
    <ol className={classes}>
      {breadcrumbs.map((breadcrumb, index, arr) => {
        const lastItem = index >= arr.length - 1;
        return (
          <ListItem
            key={getBreadcrumbObject(breadcrumb).id}
            breadcrumb={breadcrumb}
            last={lastItem}
            onSelect={onSelect}
            blockClassName={blockClassName}
            setWidth={itemWidthCallback}
          />
        );
      })}
    </ol>
  );
};

Breadcrumbs.propTypes = {
  /** Classi css relative al Block (vedi BEM). Ad uso INTERNO. */
  blockClassName: PropTypes.string,

  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,

  /**
Callback attivata alla selezione di un breadcrumb.
Il primo parametro è la breadcrumb.
   */
  onSelect: PropTypes.func,

  /**
Lista delle breadcrumbs. Può essere una lista di stringhe o di oggetti con almento le proprietà id e label.
   */
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
  ).isRequired,

  /**
   * Dimensione massima prima di collassare gli elementi compresi tra il primo e l'ultimo (se non definito non collassa).
   */
  maxWidth: PropTypes.number,
};

Breadcrumbs.defaultProps = {
  onSelect: NOOP,
  blockClassName: 'v2-breadcrumbs',
  className: undefined,
  maxWidth: undefined,
};

export default Breadcrumbs;
